/**
 * spotHistoryModel — pure functions behind the Product Spot History grid.
 *
 * Input is the `/products/{upc}/spot-history` payload (see
 * `docs/backend/product-spot-history-spec.md`):
 *   { upc, description, truncated, spots: [...], products: [{ ..., spotIds }] }
 *
 * The API returns every retailer and the UI filters by retailer client-side, so
 * switching the retailer is instant. Everything here is side-effect free so it
 * can be unit-tested without React (see spotHistoryModel.test.js).
 */

/** Sentinel for "no retailer filter". */
export const ALL_RETAILERS = '__all__';

/**
 * Start-date presets, mirroring the Tableau "Start Date" dropdown.
 * Default is the Tableau default: last 4 years.
 */
export const HISTORY_RANGES = [
  { value: '6m', label: 'Last 6 months', months: 6 },
  { value: '1y', label: 'Last year', months: 12 },
  { value: '2y', label: 'Last 2 years', months: 24 },
  { value: '4y', label: 'Last 4 years', months: 48 },
];

export const DEFAULT_HISTORY_RANGE = '4y';

function toISODay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Turn a preset into a concrete `{ from, to }` in LOCAL dates.
 * (`toISOString()` would roll to tomorrow for a PR user after 8PM.)
 *
 * @param {string} value - one of HISTORY_RANGES[].value
 * @param {Date} [today]
 */
export function resolveHistoryRange(value, today = new Date()) {
  const preset = HISTORY_RANGES.find((r) => r.value === value)
    || HISTORY_RANGES.find((r) => r.value === DEFAULT_HISTORY_RANGE);
  const from = new Date(today.getFullYear(), today.getMonth() - preset.months, today.getDate());
  return { from: toISODay(from), to: toISODay(today) };
}

/** Case/space-insensitive key so "Amigo " and "amigo" are the same retailer. */
export function retailerKey(name) {
  return String(name ?? '').trim().toLowerCase();
}

/**
 * Map the ProductsManager dropdown value to this view's retailer selection.
 * 'All Retailers' (or empty) → ALL_RETAILERS.
 */
export function initialRetailerSelection(dropdownValue) {
  if (!dropdownValue || dropdownValue === 'All Retailers') return ALL_RETAILERS;
  return dropdownValue;
}

/**
 * Retailer options with spot counts, alphabetical.
 *
 * The preselected retailer is always included (with 0 spots if absent) so the
 * user can see *why* the grid is empty instead of the selection silently
 * vanishing.
 *
 * @param {Array} spots
 * @param {string} [selected]
 * @returns {Array<{ value: string, label: string, count: number }>}
 */
export function retailerOptions(spots = [], selected = ALL_RETAILERS) {
  const byKey = new Map();
  for (const spot of spots) {
    const key = retailerKey(spot.customername);
    if (!key) continue;
    const entry = byKey.get(key) || { value: spot.customername.trim(), label: spot.customername.trim(), count: 0 };
    entry.count += 1;
    byKey.set(key, entry);
  }

  if (selected !== ALL_RETAILERS && !byKey.has(retailerKey(selected))) {
    byKey.set(retailerKey(selected), { value: selected, label: selected, count: 0 });
  }

  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** Spots newest-first (startdate DESC, then spotId DESC), like the Tableau columns. */
export function sortSpots(spots = []) {
  return [...spots].sort((a, b) => {
    if (a.startdate !== b.startdate) return a.startdate < b.startdate ? 1 : -1;
    return b.spotId - a.spotId;
  });
}

/**
 * Build the grid model for the current retailer selection.
 *
 * Rows: selected product first, then by number of shared spots (desc), then
 * description. Products that share no visible spot are dropped.
 *
 * @param {object|null} data - API payload
 * @param {{ retailer?: string, selectedUpc?: string }} options
 * @returns {{ columns: Array, rows: Array<{ upc, description, brand, size, spotIds: Set<number>, count: number, isSelected: boolean }> }}
 */
export function buildSpotHistoryGrid(data, { retailer = ALL_RETAILERS, selectedUpc } = {}) {
  if (!data) return { columns: [], rows: [] };

  const wanted = retailer === ALL_RETAILERS ? null : retailerKey(retailer);
  const columns = sortSpots(
    (data.spots || []).filter((s) => !wanted || retailerKey(s.customername) === wanted)
  );
  const visible = new Set(columns.map((c) => c.spotId));
  const selectedKey = String(selectedUpc ?? data.upc ?? '');

  const rows = [];
  for (const product of data.products || []) {
    const spotIds = new Set((product.spotIds || []).filter((id) => visible.has(id)));
    if (!spotIds.size) continue;
    rows.push({
      upc: product.upc,
      description: product.description || '',
      brand: product.brand || '',
      size: product.size || '',
      spotIds,
      count: spotIds.size,
      isSelected: String(product.upc) === selectedKey,
    });
  }

  rows.sort((a, b) => {
    if (a.isSelected !== b.isSelected) return a.isSelected ? -1 : 1;
    if (a.count !== b.count) return b.count - a.count;
    return a.description.localeCompare(b.description);
  });

  return { columns, rows };
}
